import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components/macro";
import { connect } from "react-redux";
import { compose } from "redux";
import { Formik } from "formik";
import moment from "moment";
import _ from "lodash";
import { withRouter } from "react-router";
import { mapCompanySelector } from "store/state/auth";
import { PrimaryButton, ButtonSizeOptions } from "@loanbossinc/component-library";
import CircularProgress from "@material-ui/core/CircularProgress";
import { usePrevious } from "helpers/use-previous";
import { DrawerNotes } from "modals/DrawerNotes/DrawerNotes";
import SimpleConfirmDialog from "components/Global/ConfirmationDialogs/SimpleConfirmDialog";
import ReportCalculationSection from "./reportCalculationsSection";

import { downloadFile } from "../../utils/api";

import { LrdRoleType } from "./constants";
import {
  getInstanceDetails,
  updateInstanceDetails,
  deleteReportGroupInstance,
  getReportingConfigItems,
  getNotes,
  putNote,
  postNote,
  deleteNote,
  updateTasks,
  addTask,
  updateAuthRecord,
  markAuthEmail,
  markAuthExternal,
  downloadReport,
  postAttachment,
  getPermissionedUser,
  getPermissionedUserByCompanyId,
  setLRDStore
} from "./lenderReportingDashboard.store";
import HeaderComponent from "./headerComponent";
import TaskHeader from "./taskHeader";
import TaskList from "./taskList";
import RgiCalculationBanner from "./rgiCalculationBanner";
import ApprovedTasks from "./approvedTasks";
import { trackLCReportSubmit } from "lib/mixpanel";

const PageContainer = styled.div`
  max-width: 1120px;
  margin: auto;
  display: flex;
  flex-direction: column;
  padding-bottom: 200px;
`;

const ImageBanner = styled.div`
  width: 100%;
  height: 320px;
`;

const DataContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 40px;
  min-height: 500px;
`;

const AddTask = styled(PrimaryButton)`
  && {
    width: 262px;
    height: 48px;
    background: #005aa9;
    margin-top: 37px;
  }
`;

const EmptyTasks = styled.div`
  width: 568px;
  height: 108px;
  margin: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: 92px;
`;

const EmptyTasksText = styled.div`
  font-family: Work Sans;
  font-style: normal;
  font-weight: normal;
  font-size: 20px;
  line-height: 23px;
  display: flex;
  align-items: center;
  text-align: center;
  color: #5f6368;
`;

const DeliverablesContainer = styled.div`
  flex: 2;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LoadingContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  padding-top: 30vh;
  width: 100%;
`;

const LenderReportingTasks = props => {
  // Begin Initialize State
  useEffect(() => {
    document.getElementById("ScrollToNav").scrollIntoView({ behavior: "smooth" });
    props.getPermissionedUserByCompanyId();
  }, []);

  useEffect(() => () => {
    props.setLRDStore({ taskDetails: {} });
  }, [props.setLRDStore]);

  useEffect(() => {
    const reportId = props?.match?.params?.reportId;
    if (reportId) {
      props.getInstanceDetails({ reportId });
      props.getReportingConfigItems({ reportId });
    }
  }, [props.match.params.reportId]);

  // End Initialize State
  const prevAuth = usePrevious(props.authentication);

  useEffect(() => {
    if (prevAuth?.user !== props.authentication.user) {
      props.getPermissionedUser({ contactId: props.authentication.user.contact.id });
    }
  }, [props.authentication]);

  // Begin Notes
  const [specificTaskNotes, setSpecificTaskNotes] = useState([]);
  useEffect(() => {
    if (props.notes?.length) {
      setSpecificTaskNotes(props.notes);
    }
  }, [props.notes]);

  const [initialValues, setInitialValues] = useState({ reportGroup: null, reportGroupInstance: null, tasks: null });
  useEffect(() => {
    if (props.taskDetails?.tasks) {
      const { tasks } = props.taskDetails;
      const sortedTasks = _.orderBy(tasks, ["taskName", "dueDate"], ["asc"]);
      setInitialValues({ ...props.taskDetails, tasks: sortedTasks });
    }

    return () => {
      setInitialValues({ reportGroup: null, reportGroupInstance: null, tasks: null });
    };
  }, [props.taskDetails]);

  const [isNotesDrawerOpen, setIsNotesDrawerOpen] = useState(false);
  const [noteTaskId, setNoteTaskId] = useState(null);
  const openNotes = task => {
    props.getNotes({ taskId: task.id });
    setNoteTaskId(task.id);
    setIsNotesDrawerOpen(true);
  };
  const closeNotes = () => {
    setIsNotesDrawerOpen(false);
    setNoteTaskId(null);
  };
  // End Notes

  // Begin Delete Task
  const [isDeleteTaskModalOpen, setIsDeleteTaskModalOpen] = useState(false);
  const [deleteTaskId, setDeleteTaskId] = useState(null);
  const [formikForDelete, setFormikForDelete] = useState(null);

  const confirmDelete = (taskId, formik) => {
    setIsDeleteTaskModalOpen(true);
    setDeleteTaskId(taskId);
    setFormikForDelete(formik);
  };
  const cancelDelete = () => {
    setIsDeleteTaskModalOpen(false);
    setDeleteTaskId(null);
    setFormikForDelete(null);
  };
  const deleteTask = () => {
    const tasks = formikForDelete.values.tasks.filter(task => task.id !== deleteTaskId);
    formikForDelete.setFieldValue("tasks", tasks);
    props.updateTasks({ taskArray: tasks, reportingGroupInstanceId: formikForDelete.values.reportGroupInstance.Items[0].id });
    cancelDelete();
  };
  // End Delete Task

  // Start Send Email
  const [expirationDate, setExpirationDate] = useState(props?.taskDetails?.emailAuth?.expirationDate);
  const [showEditView, setShowEditView] = useState(false);
  const [isApproved, setIsApproved] = useState(null);

  useEffect(() => {
    setExpirationDate(props.taskDetails?.emailAuth?.expirationDate ? props?.taskDetails?.emailAuth?.expirationDate : moment().add(30, "days").toDate());

  }, [props?.taskDetails?.emailAuth?.expirationDate]);

  useEffect(() => {
    if (props.taskDetails?.reportGroupInstance?.Items[0]) {
      setIsApproved(props?.taskDetails?.reportGroupInstance?.Items[0].isApproved);
    }
  }, [props.taskDetails]);

  // End Send Email

  // Begin Common Functions
  const [canApprove, setCanApprove] = useState(false);
  const [isPrivilegedUser, setIsPrivilegedUser] = useState(false);
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [isLrdAdmin, setIsLrdAdmin] = useState(false);
  const [isLrdUser, setIsLrdUser] = useState(false);
  const [isLrdUserApprover, setIsLrdUserApprover] = useState(false);
  const [isLrdUserPrivileged, setIsLrdUserPrivileged] = useState(false);

  useEffect(() => {
    const lrdUser = props.permissionedUser?.lenderReportingRoleTypeId === LrdRoleType.USER;
    setIsLrdAdmin(props.permissionedUser?.lenderReportingRoleTypeId === LrdRoleType.ADMIN);
    setIsLrdUser(lrdUser);

    if (lrdUser) {
      setIsLrdUserApprover(props.permissionedUser?.isApprover);
      setIsLrdUserPrivileged(props.permissionedUser?.isPrivileged);
    }

    if (props.authentication) {
      setIsSystemAdmin(props.authentication?.isSystemAdmin);
    }
  }, [props.permissionedUsers, props.authentication]);

  useEffect(() => {
    setCanApprove(isLrdAdmin || isSystemAdmin || isLrdUserApprover);
    setIsPrivilegedUser(isLrdAdmin || isSystemAdmin || isLrdUserPrivileged);
  }, [isSystemAdmin, isLrdAdmin, isLrdUser, isLrdUserApprover, isLrdUserPrivileged]);

  useEffect(() => {
    const { tasks } = initialValues;
    if (tasks) {
      const unprivilegedTasks = tasks?.filter(task => !task.isPrivileged);
      const sortedTasksList = isPrivilegedUser ? tasks : unprivilegedTasks;
      setInitialValues({ ...props.taskDetails, tasks: sortedTasksList });
    }
  }, [isPrivilegedUser, setInitialValues]);

  const _addTask = (formik, isExtraTask) => {
    const reportGroupInstance = formik.values.reportGroupInstance.Items[0];
    const reportGroup = formik.values.reportGroup;
    props.addTask({
      reportingGroupInstanceId: reportGroupInstance.id,
      dealId: reportGroup.dealId,
      isExtraTask
    });
  };

  const statusChange = (task, formik, value) => {
    formik.values.tasks.forEach(x => {
      if (x.id === task.id) {
        x.statusId = value;
      }
    });
    props.updateTasks({ taskArray: formik.values.tasks, reportingGroupInstanceId: formik.values.reportGroupInstance.Items[0].id });
  };

  const unapproveReport = formikProps => {
    const rgi = formikProps.values.reportGroupInstance.Items[0];
    rgi.isSubmitted = false;
    rgi.isApproved = false;
    props.updateInstanceDetails({
      reportId: formikProps.values.reportGroupInstance.Items[0].id,
      reportGroupInstance: rgi
    });
  };

  const submitReport = formikProps => {
    const rgi = formikProps.values.reportGroupInstance.Items[0];
    rgi.isSubmitted = true;
    rgi.isApproved = true;
    rgi.isLocked = true;
    rgi.isPreviousSubmission = true;
    props.updateInstanceDetails({
      reportId: formikProps.values.reportGroupInstance.Items[0].id,
      reportGroupInstance: rgi
    });
    trackLCReportSubmit();
  };

  const goToReportSetup = formikProps => {
    const rgId = formikProps.values.reportGroup.id;
    const dealId = formikProps.values.reportGroup.dealId;
    props.history.push(`/deal/${dealId}/lender-reports-setup/${rgId}`, { from: "lender-reporting-tasks" });
  };

  const _updateAuthRecord = _expirationDate => {
    setExpirationDate(_expirationDate);
    const rgiId = props?.match?.params?.reportId;
    props.updateAuthRecord({ rgiId, expirationDate: _expirationDate });
  };

  const setAuthAsExternal = () => {
    const rgiId = props?.match?.params?.reportId;
    props.markAuthExternal({ rgiId });
  };

  const setAuthAsEmail = () => {
    const rgiId = props?.match?.params?.reportId;
    props.markAuthEmail({ rgiId });
  };

  const _downloadReport = () => {
    const rgiId = props?.match?.params?.reportId;
    props.downloadReport({ rgiId });
  };

  const handleDownload = fileId => {
    downloadFile(`files/${fileId}`);
  };

  const showReportCalculations = useMemo(() => {
    if (!props.taskDetails?.reportGroup) {
      return null;
    }

    const { reportGroup, reportComponentsConfig } = props.taskDetails;
    const { shouldCalculateDscr, shouldCalculateDebtYield } = reportGroup;

    if (!shouldCalculateDscr && !shouldCalculateDebtYield) {
      return false;
    }

    const noiComponent = reportComponentsConfig.find(repComponent => repComponent.type === "NOI");
    const dscrComponent = reportComponentsConfig.find(repComponent => repComponent.type === "DSCR");
    const debtYieldComponent = reportComponentsConfig.find(repComponent => repComponent.type === "DebtYield");

    if (!noiComponent) {
      return false;
    }

    return !((shouldCalculateDscr && !dscrComponent) || (shouldCalculateDebtYield && !debtYieldComponent));
  }, [props.taskDetails]);

  const showReportCalculationsForApproved = useMemo(() => {
    if (!props.taskDetails?.reportGroupInstance) {
      return null;
    }

    const rgiReportComponentInstances = props.taskDetails.reportGroupInstance.Items[0].reportComponentInstances;

    const noiComponent = rgiReportComponentInstances.find(repComponent => repComponent.type === "NOI");
    const dscrComponent = rgiReportComponentInstances.find(repComponent => repComponent.type === "DSCR");
    const debtYieldComponent = rgiReportComponentInstances.find(repComponent => repComponent.type === "DebtYield");

    if (!noiComponent) {
      return false;
    }

    return (!!dscrComponent || !!debtYieldComponent);
  }, [props.taskDetails]);
  // End Common Functions

  const borrowingEntities = props?.taskDetails?.tasks?.flatMap(task => task.deal?.borrowingEntities).filter((str, i, arr) => arr.indexOf(str) === i);
  const filterMarkedDelieverables = files => files.filter(({ isPrivate }) => !isPrivate);

  if (!props.taskDetails?.tasks || isApproved === null) {
    return (
      <LoadingContainer>
        <CircularProgress size="3%" thickness={2} />
      </LoadingContainer>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validate={() => ({})}
      onSubmit={(values, { setSubmitting }) => {
        props.updateTasks({ taskArray: values.tasks, reportingGroupInstanceId: values.reportGroupInstance.Items[0].id });
        setSubmitting(false);
      }}
      render={formikProps => {
        return (
          <PageContainer>
            <ImageBanner>
              <HeaderComponent banner={props?.company?.bannerImage} logo={props?.company?.logoImage} />
            </ImageBanner>
            {isApproved && (
              <ApprovedTasks {...props}
                showReportCalculations={showReportCalculationsForApproved}
                taskDetails={props.taskDetails}
                borrowingEntities={borrowingEntities}
                expirationDate={expirationDate}
                updateAuthRecord={_updateAuthRecord}
                downloadReport={_downloadReport}
                canApprove={canApprove}
                setAuthAsExternal={setAuthAsExternal}
                setAuthAsEmail={setAuthAsEmail}
                setShowEditView={() => {
                  unapproveReport(formikProps);
                }}
                submitReport={() => submitReport(formikProps)}
                rgiId={formikProps?.values?.reportGroupInstance?.Items[0].id}
                formikProps={formikProps}
                isPrivilegedUser={isPrivilegedUser}
                handleDownload={handleDownload}
                filterMarkedDelieverables={filterMarkedDelieverables}
              />
          )}

          {isApproved !== null && !isApproved && (
            <>
              <DrawerNotes
                isOpen={isNotesDrawerOpen}
                notes={specificTaskNotes}
                authentication={props.authentication}
                closeDrawer={closeNotes}
                onAdd={note => props.postNote({ note, noteTaskId })}
                onUpdate={note => props.putNote({ note, noteTaskId })}
                onRemove={note => props.deleteNote({ noteId: note.id, noteTaskId })}
                type="task"
              />

              <SimpleConfirmDialog
                title="Confirm Delete"
                message="Are you sure you want to delete this task? This cannot be undone."
                modalOpen={isDeleteTaskModalOpen}
                handleNo={cancelDelete}
                handleYes={deleteTask}
                noText="No"
                yesText="Yes"
              />

              <TaskHeader
                authenticatedUser={props.authentication.user}
                data={formikProps.values}
                addTask={() => _addTask(formikProps, true)}
                history={props.history}
                configureReport={() => goToReportSetup(formikProps)}
                canApprove={canApprove}
                canDeleteReportGroupInstance={isSystemAdmin}
                deleteReportGroupInstance={props.deleteReportGroupInstance}
                updateInstanceDetails={props.updateInstanceDetails}
                updateTasks={props.updateTasks}
                company={props.company}
              />
              {!showReportCalculations && <RgiCalculationBanner history={props.history} routeData={props?.taskDetails} />}
              <DataContainer>
                <>
                {showReportCalculations && <ReportCalculationSection history={props.history} taskDetails={props.taskDetails} updateInstanceDetails={props.updateInstanceDetails} />}
                  {(formikProps.values?.tasks?.length === 0) && (
                    <EmptyTasks>
                      <EmptyTasksText>LoanBoss can create tasks based on existing loan details.</EmptyTasksText>
                      <AddTask label={"Create tasks"} size={ButtonSizeOptions.smFluid} onClick={() => _addTask(formikProps, false)} />
                    </EmptyTasks>
                  )}
                  {formikProps.values.tasks && formikProps.values.tasks.length > 0 && (
                    <TaskList
                      configItems={props.configItems}
                      tasks={formikProps?.values?.tasks}
                      isPrivilegedUser={isPrivilegedUser}
                      openNotes={openNotes}
                      canApprove={canApprove}
                      handleCancel={formikProps.handleReset}
                      handleSubmit={formikProps.handleSubmit}
                      onDelete={confirmDelete}
                      onStatusChange={statusChange}
                    />
                    )}
                </>
              </DataContainer>
            </>
          )}
        </PageContainer>
      );
      }}
    />
  );
};

const mapStateToProps = ({ lenderReportingDashboard, authentication }) => ({
  ...lenderReportingDashboard,
  company: mapCompanySelector(authentication),
  authentication
});

export default compose(
  withRouter,
  connect(mapStateToProps, {
    getInstanceDetails,
    updateInstanceDetails,
    deleteReportGroupInstance,
    getReportingConfigItems,

    getNotes,
    putNote,
    postNote,
    deleteNote,

    updateTasks,
    addTask,
    updateAuthRecord,
    markAuthEmail,
    markAuthExternal,
    downloadReport,
    postAttachment,
    getPermissionedUser,
    getPermissionedUserByCompanyId,
    setLRDStore
  })
)(LenderReportingTasks);
